import { jsx as _jsx } from "react/jsx-runtime";
import { reportRoles, } from "@gearbox-protocol/liquidator-v2-config";
import { configureStore } from "@reduxjs/toolkit";
import { createContext, useMemo } from "react";
import { createDispatchHook, createSelectorHook, createStoreHook, Provider, } from "react-redux";
import { HydratedSDKProvider } from "../../../sdk";
import { collapseKey, firstTrackOfRole } from "./helpers";
import { optimistReducer } from "./slice";
function useInitialStore(report, traceFilesPath, marketConfigurator, initialRole) {
    return useMemo(() => {
        const { tracks, error } = report;
        if (error) {
            throw new Error(error);
        }
        const roles = reportRoles(report);
        const role = initialRole && roles.includes(initialRole) ? initialRole : roles[0];
        const trackFilters = Object.fromEntries(tracks.map(t => [t.id, true]));
        // When report is loaded, if url contains hash (of manager and/or account) - open corresponding panels
        const collapsedPanels = {};
        const [creditManager, creditAccount] = window.location.hash?.slice(1)?.toLowerCase()?.split("_") ?? [];
        if (creditManager) {
            collapsedPanels[creditManager] = true;
        }
        if (creditAccount) {
            collapsedPanels[collapseKey(creditManager, creditAccount)] = true;
        }
        return configureStore({
            reducer: optimistReducer,
            preloadedState: {
                report,
                role,
                selectedTrackId: firstTrackOfRole(tracks, role, trackFilters) ?? tracks[0].id,
                collapsedPanels,
                errorsOnly: false,
                errorFilters: [],
                trackFilters,
                traceFilesPath,
                marketConfigurator,
            },
            middleware: getDefaultMiddleware => getDefaultMiddleware({
                // report is a large, read-only blob that contains BigInt balances
                // and SDK state; no reducer mutates it, so exclude it from the checks
                serializableCheck: { ignoredPaths: ["report"] },
                immutableCheck: { ignoredPaths: ["report.sdkState"] },
            }),
        });
    }, [report, traceFilesPath, marketConfigurator, initialRole]);
}
const OptimistReportCtx = createContext(null);
// Export your custom hooks if you wish to use them in other files.
export const useOptimistReportStore = createStoreHook(OptimistReportCtx);
export const useOptimistReportDispatch = createDispatchHook(OptimistReportCtx);
export const useOptimistReportSelector = createSelectorHook(OptimistReportCtx);
export const OptimistReportProvider = props => {
    const { children, report, traceFilesPath, marketConfigurator, initialRole } = props;
    const store = useInitialStore(report, traceFilesPath, marketConfigurator, initialRole);
    return (_jsx(Provider, { context: OptimistReportCtx, store: store, children: _jsx(HydratedSDKProvider, { state: report.sdkState, children: children }) }));
};
